/**
 * Service Worker 注册与更新生命周期。
 *
 * ⚠️ 必须写进验收清单的一条事实：**用户看到的是旧 SW，直到 PWA 完全退出重开。**
 * 浏览器里刷新两次、或把 PWA 从后台彻底关掉再打开，新 SW 才接管。
 * `skipWaiting()` + `clients.claim()` 能缓解，但不能消除。
 * 不知道这条，你会花半小时排查一个"已经修好了的" bug。
 *
 * 更新策略两选一：
 * - 'prompt'（默认）：发现新版本 → 通知应用 → 由用户点"立即更新" → skipWaiting + reload。
 *   数据不会在用户打字打到一半时被换掉。
 * - 'auto'：发现即接管并刷新。适合内容型应用，不适合有未提交输入的应用。
 */
import { SW_MSG, isSwMessage } from './messages.js';
import { failFrom, ok } from './result.js';
export async function registerServiceWorker(options = {}) {
    const { url = '/sw.js', scope = '/', type = 'classic', strategy = 'prompt', checkIntervalMs = 60 * 60 * 1000, } = options;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
        return { ok: false, reason: 'unsupported' };
    }
    let registration = null;
    let waiting = null;
    let reloading = false;
    let timer = null;
    let disposed = false;
    const onVisible = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible')
            void controller.check();
    };
    // 定义在外层作用域，不是 try 块里：dispose() 要摘得掉它，
    // 而块级函数声明在外面是拿不到的（这一条编译器会当场告诉你，但只有真去写 dispose 才会知道）。
    const onControllerChange = () => {
        options.onControllerChange?.();
        waiting = null;
        // 首次安装造成的接管：不刷。刷了只是白闪一下，还会丢掉用户刚填的东西。
        if (!hadController)
            return;
        // 只刷一次。不设这个闸，多标签页下会互相触发形成刷新风暴。
        if (reloading)
            return;
        reloading = true;
        if (typeof location !== 'undefined')
            location.reload();
    };
    /**
     * 注册前是否已经有 SW 在控制本页。
     *
     * ⚠️ 这个判断决定了要不要在 controllerchange 时刷新页面：
     * **首次访问**时 controller 从 null 变成新 SW（`clients.claim()` 造成的），
     * 也会触发 controllerchange——这时候刷新是纯粹的浪费，用户会看到首屏白闪一下，
     * 而且如果他正在填第一个表单，填的东西就没了。
     * 只有"本来就有一个旧 SW，现在换人了"才需要刷新。
     */
    const hadController = typeof navigator !== 'undefined' && !!navigator.serviceWorker.controller;
    const controller = {
        registration: () => registration,
        updateAvailable: () => waiting !== null,
        async check() {
            try {
                await registration?.update();
            }
            catch (e) {
                options.onError?.(e);
            }
        },
        async applyUpdate() {
            const w = waiting ?? registration?.waiting ?? null;
            if (!w) {
                // 没有等待中的 SW —— 直接刷新也无害，用户点了"更新"总得有反应
                if (typeof location !== 'undefined')
                    location.reload();
                return;
            }
            w.postMessage({ type: SW_MSG.SKIP_WAITING });
            // 剩下的交给 controllerchange
        },
        async version(timeoutMs = 2000) {
            const active = navigator.serviceWorker.controller;
            if (!active)
                return null;
            return new Promise((resolve) => {
                const channel = new MessageChannel();
                const timer = setTimeout(() => {
                    channel.port1.close();
                    resolve(null);
                }, timeoutMs);
                channel.port1.onmessage = (event) => {
                    clearTimeout(timer);
                    channel.port1.close();
                    const data = event.data;
                    resolve(isSwMessage(data) && data.type === SW_MSG.VERSION ? data : null);
                };
                active.postMessage({ type: SW_MSG.GET_VERSION }, [channel.port2]);
            });
        },
        async unregister() {
            if (!registration)
                return false;
            try {
                return await registration.unregister();
            }
            catch {
                return false;
            }
        },
        dispose() {
            if (disposed)
                return;
            disposed = true;
            if (timer !== null) {
                clearInterval(timer);
                timer = null;
            }
            if (typeof document !== 'undefined')
                document.removeEventListener('visibilitychange', onVisible);
            if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
                navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
            }
        },
    };
    function trackInstalling(sw) {
        if (!sw)
            return;
        sw.addEventListener('statechange', () => {
            // installed + 已有 controller = 这是一次"更新"，不是首次安装
            if (sw.state === 'installed' && navigator.serviceWorker.controller) {
                waiting = sw;
                if (strategy === 'auto')
                    void controller.applyUpdate();
                else
                    options.onUpdateAvailable?.(controller);
            }
        });
    }
    try {
        const reg = await navigator.serviceWorker.register(url, { scope, type });
        registration = reg;
        options.onRegistered?.(reg);
        if (reg.waiting && navigator.serviceWorker.controller) {
            // 上次没更新完（用户没点、或点了之后没刷成功），这次进来先报告
            waiting = reg.waiting;
            if (strategy === 'auto')
                void controller.applyUpdate();
            else
                options.onUpdateAvailable?.(controller);
        }
        trackInstalling(reg.installing);
        reg.addEventListener('updatefound', () => trackInstalling(reg.installing));
        navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
        if (checkIntervalMs > 0) {
            timer = setInterval(() => void controller.check(), checkIntervalMs);
            // 回到前台时也查一次：PWA 常常几天不关，光靠 interval 不够及时
            if (typeof document !== 'undefined') {
                document.addEventListener('visibilitychange', onVisible);
            }
        }
        return ok(controller);
    }
    catch (e) {
        options.onError?.(e);
        return failFrom('failed', e);
    }
}
//# sourceMappingURL=update.js.map